/**
 * useOneClickVideo — Encapsulates all one-click AI video generation logic.
 *
 * Extracted from RecordingFeature so the AIVideoButton can live independently
 * in the header bar while sharing state with VideoSettings modal.
 *
 * On completion, writes the video Blob to pendingVideoBlobAtom so that
 * RecordingFeature can pick it up and trigger the download/discard UI.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { useSetAtom } from "../app-jotai";
import { useAppI18n } from "./useAppI18n";
import { getBackendUrl } from "../utils/backendUrl";
import { pendingVideoBlobAtom } from "./useAIVideoSignal";
import type { VideoGenSettings } from "./useRecordingState";
import { getCanvasElements, getCanvasImage } from "../utils/canvasDataBridge";
import { blobToDataURL } from "../utils/screenshotHelper";
import { supabase } from "../supabaseClient";

export interface OneClickVideoState {
  isLoading: boolean;
  elapsedSeconds: number;
  showToast: boolean;
  isVideoSettingsOpen: boolean;
  hasCanvasElements: boolean;
  voiceSampleUrlBuilder: ((voice: string) => string) | undefined;

  openVideoSettings: () => void;
  closeVideoSettings: () => void;
  startGeneration: (settings: VideoGenSettings) => void;
}

export const useOneClickVideo = (): OneClickVideoState => {
  const { langCode } = useAppI18n();
  const setPendingVideoBlob = useSetAtom(pendingVideoBlobAtom);
  const [isLoading, setIsLoading] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [isVideoSettingsOpen, setIsVideoSettingsOpen] = useState(false);
  const genStartRef = useRef<number>(0);

  useEffect(() => {
    if (!isLoading) {
      setElapsedSeconds(0);
      return;
    }
    const id = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - genStartRef.current) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [isLoading]);

  useEffect(() => {
    if (!showToast) return;
    const id = setTimeout(() => setShowToast(false), 3000);
    return () => clearTimeout(id);
  }, [showToast]);

  const elements = getCanvasElements();
  const hasCanvasElements = elements.length > 0;

  const backendUrl = getBackendUrl();
  const sampleLang = langCode.startsWith("zh") ? "zh" : "en";
  const voiceSampleUrlBuilder = backendUrl
    ? (voice: string) =>
        `${backendUrl}/v1/ai/video/voice-sample?voice=${encodeURIComponent(voice)}&lang=${sampleLang}`
    : undefined;

  const openVideoSettings = useCallback(() => setIsVideoSettingsOpen(true), []);
  const closeVideoSettings = useCallback(() => setIsVideoSettingsOpen(false), []);

  const startGeneration = useCallback(
    async (settings: VideoGenSettings) => {
      if (!backendUrl) {
        alert("Backend not configured.");
        return;
      }
      const blob = await getCanvasImage();
      if (!blob) {
        alert("Could not capture canvas image. Ensure the canvas has elements.");
        return;
      }
      if (elements.length === 0) {
        alert("Canvas is empty. Add elements first.");
        return;
      }
      setIsVideoSettingsOpen(false);
      setIsLoading(true);
      genStartRef.current = Date.now();
      setShowToast(true);
      const debug = (msg: string, data?: unknown) => {
        console.log("[one_click_video]", msg, data !== undefined ? data : "");
      };
      try {
        let userId: string | undefined;
        if (supabase) {
          const { data } = await supabase.auth.getUser();
          userId = data?.user?.id ?? undefined;
        }
        if (!userId) {
          throw new Error("Please sign in to generate videos.");
        }

        const dataUrl = await blobToDataURL(blob);
        const canvasImageBase64 = dataUrl.includes(",")
          ? dataUrl.split(",")[1]!
          : dataUrl;
        const canvasElements = [...elements].map((el) => ({ ...el }));

        debug("request start", {
          canvasElements: canvasElements.length,
          imageBase64Len: canvasImageBase64.length,
          ...settings,
        });
        const res = await fetch(`${backendUrl}/v1/ai/video/one-click`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            canvasImageBase64,
            canvasElements,
            quality: settings.quality,
            targetDurationMinutes: settings.targetDurationMinutes ?? undefined,
            voice: settings.voice || undefined,
            userId,
          }),
        });
        debug("response", {
          ok: res.ok,
          status: res.status,
          statusText: res.statusText,
        });
        if (!res.ok) {
          const text = await res.text();
          let detail = res.statusText;
          try {
            const err = JSON.parse(text) as { detail?: string };
            detail = err.detail ?? detail;
          } catch {
            if (text) detail = text.slice(0, 200);
          }
          debug("error body", { detail, textLen: text.length });
          throw new Error(detail || `HTTP ${res.status}`);
        }
        const videoBlob = await res.blob();
        debug("blob received", { size: videoBlob.size, type: videoBlob.type });
        if (videoBlob.size === 0) {
          throw new Error(
            "Video file is empty (0 bytes). Backend may have failed.",
          );
        }
        setPendingVideoBlob(videoBlob);
      } catch (e) {
        const msg =
          e instanceof Error ? e.message : "Failed to generate video.";
        debug("catch", msg);
        alert(msg);
      } finally {
        setIsLoading(false);
      }
    },
    [elements, backendUrl, setPendingVideoBlob],
  );

  return {
    isLoading,
    elapsedSeconds,
    showToast,
    isVideoSettingsOpen,
    hasCanvasElements,
    voiceSampleUrlBuilder,
    openVideoSettings,
    closeVideoSettings,
    startGeneration,
  };
};
